import * as React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { services } from "../../../services";
import { alertSuccess } from "../../../store/reducers/Alert";
import { useBackgroundPromiseWrapper } from "../../../helpers/hooks/useBackgroundPromiseWrapper";
import { RegistrationRequest } from "../models/RegistrationRequest";
import { AuthLayout } from "../../../components/shared/AuthLayout/AuthLayout";
import { FormField } from "../../../components/shared/FormField/FormField";
import { AuthButton } from "../../../components/shared/AuthButton/AuthButton";
import authStyles from "../../../components/shared/AuthLayout/AuthLayout.module.css";
import styles from "./RegistrationPage.module.css";

const emptyRegistration: RegistrationRequest = {
  LastName: "",
  FirstName: "",
  MiddleName: "",
  Email: "",
  Inn: "",
  Kpp: "",
};

const RegistrationBrandContent: React.FC = () => (
  <>
    <span className={authStyles.brandBadge}>
      <span className={authStyles.brandBadgeDot} />
      Регистрация контрагента
    </span>
    <h1 className={authStyles.brandHeadline}>
      Присоединяйтесь<br />к порталу cpda
    </h1>
    <p className={authStyles.brandSubtitle}>
      Создайте учётную запись организации, чтобы согласовывать разделы,
      вести реестр замечаний и отслеживать готовность проектов.
    </p>
    <div className={`${authStyles.glassCard} ${styles.stepsCard}`}>
      <div className={styles.stepsTitle}>Как это работает</div>
      <div className={styles.stepsList}>
        {[
          "Заполните данные организации",
          "Подтвердите email",
          "Получите доступ к проектам",
        ].map((text, i) => (
          <div key={i} className={styles.stepItem}>
            <span className={styles.stepNum}>{i + 1}</span>
            <span className={styles.stepLabel}>{text}</span>
          </div>
        ))}
      </div>
    </div>
  </>
);

export const RegistrationPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [submit, isLoading] = useBackgroundPromiseWrapper();
  const [data, setData] = React.useState<RegistrationRequest>(emptyRegistration);
  const [errorKeys, setErrorKeys] = React.useState<Set<keyof RegistrationRequest>>(new Set());
  const [registered, setRegistered] = React.useState(false);
  const [agree, setAgree] = React.useState(false);

  const update = (field: keyof RegistrationRequest, value: string) => {
    setData(prev => ({
      ...prev,
      [field]: field === "Inn" || field === "Kpp" ? value.replace(/\D/g, "") : value,
    }));
  };

  const validate = () => {
    const errors = new Set<keyof RegistrationRequest>();
    if (!data.LastName.trim()) errors.add("LastName");
    if (!data.FirstName.trim()) errors.add("FirstName");
    if (!data.Email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.Email.trim())) errors.add("Email");
    if (![10, 12].includes(data.Inn.length)) errors.add("Inn");
    if (data.Kpp.length !== 9) errors.add("Kpp");
    setErrorKeys(errors);
    return errors.size === 0;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    submit(
      () => services.authService.register({
        ...data,
        LastName: data.LastName.trim(),
        FirstName: data.FirstName.trim(),
        MiddleName: data.MiddleName?.trim(),
        Email: data.Email.trim().toLowerCase(),
      }),
      () => {
        setRegistered(true);
        dispatch(alertSuccess({
          detail: "Учетная запись создана. Письмо для установки пароля отправлено.",
        }));
      }
    );
  };

  const inputClass = (field: keyof RegistrationRequest) =>
    `${styles.input}${errorKeys.has(field) ? ` ${styles.inputError}` : ""}`;

  return (
    <AuthLayout brandContent={<RegistrationBrandContent />}>
      <div className={styles.formWrap}>
        {registered ? (
          <div className={styles.successCard}>
            <div className={styles.successIcon}>
              <i className="fa-solid fa-envelope-circle-check" />
            </div>
            <h2 className={styles.successTitle}>Учётная запись создана</h2>
            <p className={styles.successText}>
              Проверьте почту и перейдите по ссылке из письма, чтобы установить пароль.
            </p>
            <button className={styles.loginBtn} onClick={() => navigate('/login')} type="button">
              <i className="fa-solid fa-right-to-bracket" />
              Войти
            </button>
          </div>
        ) : (
          <>
            <h2 className={styles.formTitle}>Регистрация</h2>
            <p className={styles.formSubtitle}>
              Заполните данные, чтобы создать учётную запись для доступа к проектной документации.
            </p>

            <form onSubmit={onSubmit} noValidate>
              <div className={styles.formFields}>
                <div className={styles.nameRow}>
                  <FormField
                    label="Фамилия"
                    error={errorKeys.has("LastName") ? "Укажите фамилию" : undefined}
                  >
                    <input
                      className={inputClass("LastName")}
                      placeholder="Иванов"
                      value={data.LastName}
                      onChange={e => update("LastName", e.currentTarget.value)}
                    />
                  </FormField>
                  <FormField
                    label="Имя"
                    error={errorKeys.has("FirstName") ? "Укажите имя" : undefined}
                  >
                    <input
                      className={inputClass("FirstName")}
                      placeholder="Иван"
                      value={data.FirstName}
                      onChange={e => update("FirstName", e.currentTarget.value)}
                    />
                  </FormField>
                </div>

                <FormField label="Отчество" optional>
                  <input
                    className={styles.input}
                    placeholder="Иванович"
                    value={data.MiddleName}
                    onChange={e => update("MiddleName", e.currentTarget.value)}
                  />
                </FormField>

                <FormField
                  label="Email"
                  error={errorKeys.has("Email") ? "Укажите корректный email" : undefined}
                >
                  <div className={styles.inputWrap}>
                    <i className={`fa-regular fa-envelope ${styles.inputIcon}`} />
                    <input
                      type="email"
                      className={`${inputClass("Email")} ${styles.inputWithIcon}`}
                      placeholder="name@company.ru"
                      value={data.Email}
                      onChange={e => update("Email", e.currentTarget.value)}
                    />
                  </div>
                </FormField>

                <div className={styles.nameRow}>
                  <FormField
                    label="ИНН"
                    error={errorKeys.has("Inn") ? "10 или 12 цифр" : undefined}
                  >
                    <input
                      inputMode="numeric"
                      className={inputClass("Inn")}
                      placeholder="10 или 12 цифр"
                      maxLength={12}
                      value={data.Inn}
                      onChange={e => update("Inn", e.currentTarget.value)}
                    />
                  </FormField>
                  <FormField
                    label="КПП"
                    error={errorKeys.has("Kpp") ? "Должен содержать 9 цифр" : undefined}
                  >
                    <input
                      inputMode="numeric"
                      className={inputClass("Kpp")}
                      placeholder="9 цифр"
                      maxLength={9}
                      value={data.Kpp}
                      onChange={e => update("Kpp", e.currentTarget.value)}
                    />
                  </FormField>
                </div>
              </div>

              <label className={styles.consent}>
                <span
                  className={`${styles.checkboxBox}${agree ? ` ${styles.checkboxBoxChecked}` : ""}`}
                  onClick={() => setAgree(v => !v)}
                >
                  {agree && <i className="fa-solid fa-check" />}
                </span>
                <span className={styles.consentText}>
                  Я согласен на обработку персональных данных и принимаю{" "}
                  <span className={styles.consentLink}>условия использования</span>{" "}
                  портала X5 CPDA.
                </span>
              </label>

              <AuthButton
                type="submit"
                loading={isLoading}
                disabled={!agree}
                className={styles.submitBtnMargin}
              >
                <i className="fa-solid fa-user-plus" />
                Зарегистрироваться
              </AuthButton>

              <p className={styles.loginHint}>
                Уже есть аккаунт?{" "}
                <button type="button" className={styles.loginLink} onClick={() => navigate('/login')}>
                  Войти
                </button>
              </p>
            </form>
          </>
        )}
      </div>
    </AuthLayout>
  );
};
